import React from 'react';

import type { CadenceFrequency } from '@/types';
import { cn } from '@/utils/classname';

/**
 * Step 1 of the Scan Schedule wizard — the cadence radiogroup (FIDELITY D1-D5).
 *
 * Accessible radiogroup transcribed from approved-mockup.html:253-262, 460-475:
 * `role="radiogroup"` + 4 `role="radio"` options with roving tabindex, arrow-key
 * navigation (Up/Left = prev, Down/Right = next, wrap), Enter/Space selects,
 * `aria-checked`. Chariot tokens only; no shadcn radio-group primitive exists,
 * and no banned native control is used.
 */

interface CadenceOption {
  freq: CadenceFrequency;
  name: string;
  desc: string;
}

// D3 — copy verbatim from FIDELITY-CHECKLIST §D3 / approved-mockup.html:254-257
const OPTIONS: CadenceOption[] = [
  { freq: 'weekly', name: 'Weekly', desc: 'Re-scan every calendar week' },
  { freq: 'biweekly', name: 'Biweekly', desc: 'Re-scan every 2 weeks' },
  { freq: 'monthly', name: 'Monthly', desc: 'Re-scan once a month' },
  { freq: 'quarterly', name: 'Quarterly', desc: 'Re-scan once a quarter' },
];

export interface CadenceStepProps {
  value: CadenceFrequency;
  onChange: (value: CadenceFrequency) => void;
}

export const CadenceStep: React.FC<CadenceStepProps> = ({
  value,
  onChange,
}) => {
  const move = (delta: number) => {
    const idx = OPTIONS.findIndex(o => o.freq === value);
    const nextIdx = (idx + delta + OPTIONS.length) % OPTIONS.length;
    onChange(OPTIONS[nextIdx].freq);
  };

  return (
    <div
      role="group"
      aria-label="Step 1: Cadence"
      data-testid="scan-schedule-wizard-step-1"
    >
      {/* D1 — field label (verbatim) */}
      <span
        id="cadence-freq-label"
        className="text-chariot-text-secondary mb-2 block text-xs font-bold tracking-wide uppercase"
      >
        How often should each target re-scan?
      </span>
      {/* D2 — help text (verbatim) */}
      <p className="text-chariot-text-secondary mb-2.5 text-xs leading-relaxed">
        A target re-scans at most once per period. Inside a period, the day/time
        window in Step&nbsp;2 picks the exact launch moment.
      </p>

      <div
        role="radiogroup"
        aria-labelledby="cadence-freq-label"
        className="grid grid-cols-1 gap-2.5 sm:grid-cols-2"
      >
        {OPTIONS.map(opt => {
          const checked = opt.freq === value;
          return (
            <div
              key={opt.freq}
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              data-testid={`cadence-radio-${opt.freq}`}
              onClick={() => onChange(opt.freq)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChange(opt.freq);
                } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                  e.preventDefault();
                  move(1);
                } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  move(-1);
                }
              }}
              className={cn(
                'border-chariot-stroke bg-chariot-layer0 flex min-h-[44px] cursor-pointer flex-col gap-0.5 rounded-md border p-3',
                'focus-visible:outline-chariot-brand-primary focus-visible:outline-2 focus-visible:outline-offset-2',
                // D4 — selected: brand-primary border + brand-tertiary fill + brand-primary label
                checked &&
                  'border-chariot-brand-primary bg-chariot-brand-tertiary'
              )}
            >
              <span
                className={cn(
                  'text-chariot-text-primary text-sm font-bold',
                  checked && 'text-chariot-brand-primary'
                )}
              >
                {opt.name}
              </span>
              <span className="text-chariot-text-secondary text-xs">
                {opt.desc}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
